import { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, AlertTriangle } from "lucide-react";

/* Save one or more fields via the override endpoint; returns the latest case. */
async function saveFields(id, fields) {
  let latest = null;
  for (const [field, value] of Object.entries(fields)) {
    latest = await api.override(id, field, value);
  }
  return latest;
}

function Shell({ open, onClose, title, desc, onSave, getWarnings, saveLabel = "Save changes", children }) {
  const [saving, setSaving] = useState(false);
  const [warnings, setWarnings] = useState(null);
  const doSave = async () => {
    setSaving(true);
    try {
      await onSave();
      toast.success("Changes saved");
      onClose();
    } catch (e) {
      toast.error("Could not save changes");
    } finally {
      setSaving(false);
      setWarnings(null);
    }
  };
  const handle = () => {
    const w = getWarnings ? getWarnings() : [];
    if (w && w.length) { setWarnings(w); return; }
    doSave();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setWarnings(null); onClose(); } }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading">{title}</DialogTitle>
          {desc && <DialogDescription>{desc}</DialogDescription>}
        </DialogHeader>
        <div className="flex flex-col gap-4 py-2">{children}</div>
        {warnings ? (
          <div className="rounded-md border border-yellow-300 bg-yellow-50 p-4" data-testid="edit-warning">
            <p className="flex items-center gap-1.5 text-sm font-semibold text-yellow-900">
              <AlertTriangle className="h-4 w-4" /> This will remove content:
            </p>
            <ul className="mt-2 list-inside list-disc text-sm text-yellow-900">
              {warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setWarnings(null)} data-testid="edit-warning-back">Go back</Button>
              <Button onClick={doSave} disabled={saving} data-testid="edit-warning-confirm" className="gap-2">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save anyway
              </Button>
            </div>
          </div>
        ) : (
          <DialogFooter>
            <Button variant="outline" onClick={onClose} data-testid="edit-cancel-btn">Cancel</Button>
            <Button onClick={handle} disabled={saving} data-testid="edit-save-btn" className="gap-2">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {saveLabel}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

const AddBtn = ({ onClick, label }) => (
  <Button type="button" variant="outline" size="sm" onClick={onClick} className="gap-1 self-start" data-testid="edit-add-row">
    <Plus className="h-3.5 w-3.5" /> {label}
  </Button>
);
const DelBtn = ({ onClick }) => (
  <button type="button" onClick={onClick} className="shrink-0 rounded-md p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
    <Trash2 className="h-4 w-4" />
  </button>
);

/* ---------- Provisional differential + impression ---------- */
export function EditProvisional({ c, setC, open, onClose }) {
  const [rows, setRows] = useState([]);
  const [impression, setImpression] = useState("");
  useEffect(() => {
    if (open) {
      setRows((c.provisional_diagnoses || []).map((d) => ({ ...d })));
      setImpression(c.impression || "");
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const upd = (i, k, v) => setRows(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  return (
    <Shell open={open} onClose={onClose} title="Edit provisional differential"
      desc="Adjust the diagnoses, probabilities and your clinical impression."
      getWarnings={() => {
        const w = [];
        if ((c.provisional_diagnoses || []).length > 0 && rows.filter((r) => r.name.trim()).length === 0)
          w.push("All provisional diagnoses");
        if ((c.impression || "").trim() && !impression.trim()) w.push("Clinical impression");
        return w;
      }}
      onSave={async () => setC(await saveFields(c.id, {
        provisional_diagnoses: rows.map((r) => ({ ...r, probability: Number(r.probability) || 0 })),
        impression,
      }))}>
      <div className="flex flex-col gap-1.5">
        <Label>Clinical impression</Label>
        <Textarea value={impression} onChange={(e) => setImpression(e.target.value)} rows={2} data-testid="edit-impression" />
      </div>
      {rows.map((r, i) => (
        <div key={i} className="flex items-start gap-2 rounded-md border border-border p-3">
          <div className="flex flex-1 flex-col gap-2">
            <div className="flex gap-2">
              <Input value={r.name} onChange={(e) => upd(i, "name", e.target.value)} placeholder="Diagnosis" />
              <Input type="number" className="w-24" value={r.probability} onChange={(e) => upd(i, "probability", e.target.value)} placeholder="%" />
            </div>
            <Textarea value={r.rationale} onChange={(e) => upd(i, "rationale", e.target.value)} rows={2} placeholder="Rationale" />
          </div>
          <DelBtn onClick={() => setRows(rows.filter((_, idx) => idx !== i))} />
        </div>
      ))}
      <AddBtn label="Add diagnosis" onClick={() => setRows([...rows, { name: "", probability: 0, rationale: "" }])} />
    </Shell>
  );
}

/* ---------- Investigations ---------- */
export function EditInvestigations({ c, setC, open, onClose }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { if (open) setRows((c.investigations || []).map((x) => ({ ...x }))); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const upd = (i, k, v) => setRows(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  return (
    <Shell open={open} onClose={onClose} title="Edit investigations"
      desc="Add, remove or modify the ordered investigations."
      getWarnings={() => ((c.investigations || []).length > 0 && rows.filter((r) => r.name.trim()).length === 0)
        ? ["All investigations"] : []}
      onSave={async () => setC(await saveFields(c.id, { investigations: rows }))}>
      {rows.map((r, i) => (
        <div key={i} className="flex items-start gap-2 rounded-md border border-border p-3">
          <div className="flex flex-1 flex-col gap-2">
            <div className="flex gap-2">
              <Input value={r.name} onChange={(e) => upd(i, "name", e.target.value)} placeholder="Investigation" />
              <Input className="w-32" value={r.category || ""} onChange={(e) => upd(i, "category", e.target.value)} placeholder="Lab/Imaging/Pathology/Other" />
            </div>
            <Input value={r.rationale || ""} onChange={(e) => upd(i, "rationale", e.target.value)} placeholder="Rationale" />
          </div>
          <DelBtn onClick={() => setRows(rows.filter((_, idx) => idx !== i))} />
        </div>
      ))}
      <AddBtn label="Add investigation" onClick={() => setRows([...rows, { name: "", category: "Other", rationale: "" }])} />
    </Shell>
  );
}

/* ---------- Final diagnosis ---------- */
export function EditFinal({ c, setC, open, onClose }) {
  const [fd, setFd] = useState({});
  useEffect(() => { if (open) setFd({ ...(c.final_diagnosis || {}) }); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const upd = (k, v) => setFd({ ...fd, [k]: v });
  return (
    <Shell open={open} onClose={onClose} title="Edit final diagnosis"
      desc="Adjust the final diagnosis, stage, confidence and rationale."
      getWarnings={() => ((c.final_diagnosis?.name || "").trim() && !(fd.name || "").trim())
        ? ["Final diagnosis name"] : []}
      onSave={async () => setC(await saveFields(c.id, { final_diagnosis: fd }))}>
      <div className="flex flex-col gap-1.5"><Label>Diagnosis</Label>
        <Input value={fd.name || ""} onChange={(e) => upd("name", e.target.value)} data-testid="edit-final-name" /></div>
      <div className="flex gap-3">
        <div className="flex flex-1 flex-col gap-1.5"><Label>Stage</Label>
          <Input value={fd.stage || ""} onChange={(e) => upd("stage", e.target.value)} /></div>
        <div className="flex flex-1 flex-col gap-1.5"><Label>Confidence</Label>
          <Input value={fd.confidence || ""} onChange={(e) => upd("confidence", e.target.value)} placeholder="High/Moderate/Low" /></div>
      </div>
      <div className="flex flex-col gap-1.5"><Label>Rationale</Label>
        <Textarea value={fd.rationale || ""} onChange={(e) => upd("rationale", e.target.value)} rows={4} /></div>
    </Shell>
  );
}

/* ---------- Care plan ---------- */
export function EditCarePlan({ c, setC, open, onClose }) {
  const [cp, setCp] = useState({});
  useEffect(() => {
    if (open) {
      const base = c.care_plan || {};
      setCp({
        goals: base.goals || "",
        treatments: (base.treatments || []).map((t) => ({ ...t })),
        prognosis: {
          outlook: base.prognosis?.outlook || "",
          five_year_survival: base.prognosis?.five_year_survival || "",
          key_factors: (base.prognosis?.key_factors || []).join("\n"),
        },
      });
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const updT = (i, k, v) => setCp({ ...cp, treatments: cp.treatments.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)) });
  const updP = (k, v) => setCp({ ...cp, prognosis: { ...cp.prognosis, [k]: v } });
  return (
    <Shell open={open} onClose={onClose} title="Edit care plan & prognosis"
      desc="Adjust goals, treatments and prognosis for this patient."
      getWarnings={() => ((c.care_plan?.treatments || []).length > 0 && (cp.treatments || []).filter((t) => (t.modality || "").trim() || (t.detail || "").trim()).length === 0)
        ? ["All treatments"] : []}
      onSave={async () => setC(await saveFields(c.id, {
        care_plan: {
          goals: cp.goals,
          treatments: cp.treatments,
          prognosis: {
            outlook: cp.prognosis.outlook,
            five_year_survival: cp.prognosis.five_year_survival,
            key_factors: cp.prognosis.key_factors.split("\n").map((s) => s.trim()).filter(Boolean),
          },
        },
      }))}>
      <div className="flex flex-col gap-1.5"><Label>Goals of care</Label>
        <Textarea value={cp.goals || ""} onChange={(e) => setCp({ ...cp, goals: e.target.value })} rows={2} /></div>
      <Label className="mt-1 font-semibold">Treatments</Label>
      {(cp.treatments || []).map((t, i) => (
        <div key={i} className="flex items-start gap-2 rounded-md border border-border p-3">
          <div className="flex flex-1 flex-col gap-2">
            <Input value={t.modality || ""} onChange={(e) => updT(i, "modality", e.target.value)} placeholder="Modality" />
            <Textarea value={t.detail || ""} onChange={(e) => updT(i, "detail", e.target.value)} rows={2} placeholder="Detail" />
            <Input value={t.evidence || ""} onChange={(e) => updT(i, "evidence", e.target.value)} placeholder="Evidence basis" />
          </div>
          <DelBtn onClick={() => setCp({ ...cp, treatments: cp.treatments.filter((_, idx) => idx !== i) })} />
        </div>
      ))}
      <AddBtn label="Add treatment" onClick={() => setCp({ ...cp, treatments: [...(cp.treatments || []), { modality: "", detail: "", evidence: "" }] })} />
      <Label className="mt-1 font-semibold">Prognosis</Label>
      <Textarea value={cp.prognosis?.outlook || ""} onChange={(e) => updP("outlook", e.target.value)} rows={2} placeholder="Outlook summary" />
      <Input value={cp.prognosis?.five_year_survival || ""} onChange={(e) => updP("five_year_survival", e.target.value)} placeholder="5-year outlook" />
      <div className="flex flex-col gap-1.5"><Label>Key factors (one per line)</Label>
        <Textarea value={cp.prognosis?.key_factors || ""} onChange={(e) => updP("key_factors", e.target.value)} rows={3} /></div>
    </Shell>
  );
}

/* ---------- Follow-up ---------- */
export function EditFollowUp({ c, setC, open, onClose }) {
  const [fu, setFu] = useState({});
  useEffect(() => {
    if (open) {
      const base = c.follow_up || {};
      setFu({
        monitoring_parameters: (base.monitoring_parameters || []).map((p) => ({ ...p })),
        schedule: (base.schedule || []).map((s) => ({ ...s })),
        red_flags: (base.red_flags || []).join("\n"),
        disposition_criteria: base.disposition_criteria || "",
      });
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const updP = (i, k, v) => setFu({ ...fu, monitoring_parameters: fu.monitoring_parameters.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)) });
  const updS = (i, k, v) => setFu({ ...fu, schedule: fu.schedule.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)) });
  return (
    <Shell open={open} onClose={onClose} title="Edit follow-up plan"
      desc="Adjust monitoring parameters, schedule, red flags and disposition."
      getWarnings={() => {
        const w = [];
        if ((c.follow_up?.monitoring_parameters || []).length > 0 && (fu.monitoring_parameters || []).filter((p) => (p.parameter || "").trim()).length === 0)
          w.push("All monitoring parameters");
        if ((c.follow_up?.schedule || []).length > 0 && (fu.schedule || []).filter((s) => (s.timepoint || "").trim()).length === 0)
          w.push("Entire follow-up schedule");
        return w;
      }}
      onSave={async () => setC(await saveFields(c.id, {
        follow_up: {
          monitoring_parameters: fu.monitoring_parameters,
          schedule: fu.schedule,
          red_flags: fu.red_flags.split("\n").map((s) => s.trim()).filter(Boolean),
          disposition_criteria: fu.disposition_criteria,
        },
      }))}>
      <Label className="font-semibold">Monitoring parameters</Label>
      {(fu.monitoring_parameters || []).map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input value={p.parameter || ""} onChange={(e) => updP(i, "parameter", e.target.value)} placeholder="Parameter" />
          <Input value={p.frequency || ""} onChange={(e) => updP(i, "frequency", e.target.value)} placeholder="Frequency" />
          <Input value={p.target || ""} onChange={(e) => updP(i, "target", e.target.value)} placeholder="Watch for" />
          <DelBtn onClick={() => setFu({ ...fu, monitoring_parameters: fu.monitoring_parameters.filter((_, idx) => idx !== i) })} />
        </div>
      ))}
      <AddBtn label="Add parameter" onClick={() => setFu({ ...fu, monitoring_parameters: [...(fu.monitoring_parameters || []), { parameter: "", frequency: "", target: "" }] })} />
      <Label className="mt-1 font-semibold">Schedule</Label>
      {(fu.schedule || []).map((s, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input className="w-40" value={s.timepoint || ""} onChange={(e) => updS(i, "timepoint", e.target.value)} placeholder="Timepoint" />
          <Input value={s.action || ""} onChange={(e) => updS(i, "action", e.target.value)} placeholder="Action" />
          <DelBtn onClick={() => setFu({ ...fu, schedule: fu.schedule.filter((_, idx) => idx !== i) })} />
        </div>
      ))}
      <AddBtn label="Add timepoint" onClick={() => setFu({ ...fu, schedule: [...(fu.schedule || []), { timepoint: "", action: "" }] })} />
      <div className="flex flex-col gap-1.5"><Label>Red flags (one per line)</Label>
        <Textarea value={fu.red_flags || ""} onChange={(e) => setFu({ ...fu, red_flags: e.target.value })} rows={3} /></div>
      <div className="flex flex-col gap-1.5"><Label>Disposition / remission criteria</Label>
        <Textarea value={fu.disposition_criteria || ""} onChange={(e) => setFu({ ...fu, disposition_criteria: e.target.value })} rows={3} /></div>
    </Shell>
  );
}


/* ---------- Record a follow-up visit (AI re-assesses prognosis) ---------- */
export function RecordVisit({ c, setC, open, onClose }) {
  const [date, setDate] = useState("");
  const [notes, setNotes] = useState("");
  const [readings, setReadings] = useState([]);
  useEffect(() => {
    if (open) {
      setDate(new Date().toISOString().slice(0, 10));
      setNotes("");
      const params = c.follow_up?.monitoring_parameters || [];
      setReadings(params.map((p) => ({ parameter: p.parameter, value: "" })));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const upd = (i, v) => setReadings(readings.map((r, idx) => (idx === i ? { ...r, value: v } : r)));
  return (
    <Shell open={open} onClose={onClose} title="Record follow-up visit"
      desc="Enter this visit's findings. The assistant will re-assess the prognosis for this patient."
      saveLabel="Save & re-assess prognosis"
      onSave={async () => setC(await api.addVisit(c.id, {
        date, notes,
        readings: readings.filter((r) => (r.value || "").toString().trim()),
      }))}>
      <div className="flex flex-col gap-1.5">
        <Label>Visit date</Label>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="visit-date" />
      </div>
      {readings.length > 0 && (
        <>
          <Label className="mt-1 font-semibold">Readings</Label>
          {readings.map((r, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="flex-1 text-sm text-foreground">{r.parameter}</span>
              <Input className="w-56" value={r.value} onChange={(e) => upd(i, e.target.value)}
                placeholder="Result / value" data-testid={`visit-reading-${i}`} />
            </div>
          ))}
        </>
      )}
      <div className="flex flex-col gap-1.5">
        <Label>Notes / symptoms</Label>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
          placeholder="How is the patient doing? New symptoms, tolerance, exam findings…" data-testid="visit-notes" />
      </div>
    </Shell>
  );
}
